import {
  AccelerantSubmission,
  AccelerantPayload,
  AccelerantResponse,
  AttachmentUploadResult,
  ReferralStatusUpdateResult,
  AccelerantReferral,
  MemberProgram,
  OrgUser,
} from "./types/Accelerant.types";
import { Result } from "@badrap/result";
import { config } from "../../config/env";

export class AccelerantService {
  private apiKey: string;
  private memberProgramsCache: MemberProgram[] | null = null;

  constructor() {
    this.apiKey = config.n8n?.apiKey || "";
  }

  async getMemberPrograms(): Promise<Result<MemberProgram[], Error>> {
    // Return cached data if available
    if (this.memberProgramsCache) {
      return Result.ok(this.memberProgramsCache);
    }

    try {
      const url = `${config.accelerant.getMemberProgramsUrl}?org_id=${config.accelerant.orgId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
      });

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          // Ignore parse error
        }
        return Result.err(
          new Error(
            `Failed to fetch member programs: HTTP ${response.status}. ${errorDetails}`
          )
        );
      }

      const result = await response.json();

      let programsData: MemberProgram[];
      if (Array.isArray(result)) {
        programsData = result[0]?.data || [];
      } else {
        programsData = result.data || [];
      }

      this.memberProgramsCache = programsData;
      return Result.ok(programsData);
    } catch (error) {
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to fetch member programs")
      );
    }
  }

  private findProgramByName(
    programs: MemberProgram[],
    programName: string
  ): MemberProgram | undefined {
    const normalizedName = programName.toUpperCase();
    return programs.find(
      (p) => p.program_name.toUpperCase() === normalizedName
    );
  }

  private extractProgramDetails(program: MemberProgram): {
    programId: string;
    products: string[];
    linesOfBusiness: string[];
  } {
    const activePairs = program.line_of_business_product_pairs.filter(
      (pair) => pair.active
    );

    const products = Array.from(
      new Set(activePairs.map((pair) => pair.product.product_id))
    );

    // If product is "Farm Owners" (PRO-000026), only use "Property" (LOB-000011)
    const linesOfBusiness = products.includes("PRO-000026")
      ? ["LOB-000011"]
      : Array.from(
          new Set(
            activePairs.map((pair) => pair.line_of_business.line_of_business_id)
          )
        );

    return {
      programId: program.program_id,
      products,
      linesOfBusiness,
    };
  }

  private async getOrgUsers(): Promise<OrgUser[]> {
    const url = `${config.accelerant.getOrgUsersUrl}?org_id=${config.accelerant.orgId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(
          `Failed to fetch organization users: HTTP ${response.status}. ${errorBody}`
        );
      }

      const result = await response.json();

      let usersData: OrgUser[];
      if (Array.isArray(result)) {
        usersData = result[0]?.data || [];
      } else {
        usersData = result.data || [];
      }

      return usersData;
    } catch (error) {
      throw error;
    }
  }

  private findUnderwriterIdByName(
    users: OrgUser[],
    underwriterName: string
  ): string {
    const normalizedName = underwriterName.toLowerCase().trim();

    // Direct match
    const directMatch = users.find(
      (user) => user.name.toLowerCase().trim() === normalizedName
    );
    if (directMatch) {
      return directMatch.id;
    }

    // Partial match (name contains or is contained by)
    for (const user of users) {
      const userName = user.name.toLowerCase().trim();
      if (
        userName.includes(normalizedName) ||
        normalizedName.includes(userName)
      ) {
        return user.id;
      }
    }

    throw new Error(
      `Underwriter "${underwriterName}" not found in Accelerant organization users`
    );
  }

  async submitApplication(
    data: AccelerantSubmission
  ): Promise<Result<AccelerantResponse, Error>> {
    try {
      const referralReasonMap: { [key: string]: string } = {
        "Hazard Exposure": "RRM-001025",
        Limits: "RRM-001034",
        "Claims Experience": "RRM-001010",
      };

      const mappedReferralReasons = data.referralReasons.map(
        (reason) => referralReasonMap[reason] || reason
      );

      let referralType: string;
      if (data.applicationType === "NEW") {
        referralType = "referral_new_business";
      } else if (data.applicationType === "REN") {
        referralType = "accelerant_renewal_referral";
      } else if (data.applicationType === "END") {
        referralType = "accelerant_mta_referral";
      } else {
        referralType = "referral_new_business";
      }

      const inceptionDate = data.effectiveDate
        ? new Date(data.effectiveDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      const expiryDate = data.expirationDate
        ? new Date(data.expirationDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      const requestedBy = new Date();
      requestedBy.setDate(requestedBy.getDate() + 2);

      // Fetch member programs from Accelerant API
      let programId = "";
      let products: string[] = [];
      let linesOfBusiness: string[] = [];

      if (data.policySystem === "surefyre" && data.program) {
        const programsResult = await this.getMemberPrograms();

        if (programsResult.isOk) {
          const matchedProgram =
            this.findProgramByName(programsResult.value, data.program) ||
            this.findProgramByName(programsResult.value, "Flex");

          if (matchedProgram) {
            const details = this.extractProgramDetails(matchedProgram);
            programId = details.programId;
            products = details.products;
            linesOfBusiness = details.linesOfBusiness;
          }
        } else {
          console.error(
            "Failed to fetch member programs:",
            programsResult.error
          );
        }
      }

      const orgUsers = await this.getOrgUsers();
      const underwriterId = data.underwriter
        ? this.findUnderwriterIdByName(orgUsers, data.underwriter)
        : orgUsers[0]?.id;

      if (!underwriterId) {
        return Result.err(
          new Error("No underwriter found in organization users")
        );
      }

      const payload: AccelerantPayload = {
        org_id: "ca90e8a7-322a-427a-aeb9-f91160d1fe36",
        member_underwriter_id: underwriterId,
        accelerant_underwriter_id: "679ffccb-3681-4c9a-be28-77697a1a302c",
        referral_type: referralType,
        state: "draft",
        insured_name: data.namedInsured,
        quote_number: data.quote,
        program_id: programId,
        products: products,
        lines_of_business: linesOfBusiness,
        referral_reasons: mappedReferralReasons,
        inception_date: inceptionDate,
        expiry_date: expiryDate,
        requested_by: requestedBy.toISOString(),
        summary_of_risk: data.summaryOfRisk,
        policy_currency: "USD",
        total_sums_insured: data.limit || 0,
      };

      const apiUrl = config.accelerant.submitReferralUrl;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          // Ignore parse error
        }
        return Result.err(
          new Error(
            `HTTP ${response.status}: ${response.statusText}. ${errorDetails}`
          )
        );
      }

      const result = await response.json();
      return Result.ok({
        success: true,
        referenceNumber: result.referenceNumber || result.id,
        referralId: result.id,
        message: result.message || "Application submitted successfully",
      });
    } catch (error) {
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to submit application")
      );
    }
  }

  async uploadAttachments(
    referralId: string,
    files: { url: string; name: string }[]
  ): Promise<Result<AttachmentUploadResult, Error>> {
    try {
      const apiUrl = `${config.accelerant.uploadAttachmentsUrl}/${referralId}`;
      const n8nFileProxyUrl = config.accelerant.fileProxyUrl;

      if (!n8nFileProxyUrl) {
        return Result.err(new Error("N8n file proxy webhook URL is not set"));
      }

      const uploadPromises = files.map(async (file) => {
        try {
          const formData = new FormData();

          const fileResponse = await fetch(n8nFileProxyUrl, {
            method: "POST",
            headers: {
              "X-API-Key": this.apiKey,
            },
            body: JSON.stringify({
              url: file.url,
            }),
          });

          if (!fileResponse.ok) {
            return {
              success: false,
              fileName: file.name,
              status: "failed" as const,
            };
          }

          const blob = await fileResponse.blob();

          let mimeType = blob.type;
          if (!mimeType || mimeType === "application/octet-stream") {
            const extension = file.name.split(".").pop()?.toLowerCase();
            switch (extension) {
              case "png":
                mimeType = "image/png";
                break;
              case "jpg":
              case "jpeg":
                mimeType = "image/jpeg";
                break;
              case "pdf":
                mimeType = "application/pdf";
                break;
              case "doc":
                mimeType = "application/msword";
                break;
              case "docx":
                mimeType =
                  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                break;
              default:
                mimeType = "application/pdf";
            }
          }

          const fileObj = new File([blob], file.name, { type: mimeType });
          formData.append("file", fileObj);
          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "X-API-Key": this.apiKey,
            },
            body: formData,
          });

          const result = await response.json();

          if (result.error) {
            try {
              if (result.error.message.includes("duplicate_attachment")) {
                return {
                  success: true,
                  fileName: file.name,
                  status: "ready" as const,
                };
              }
            } catch (e) {}
            return {
              success: false,
              fileName: file.name,
              status: "failed" as const,
            };
          } else {
            if (result.status === "ready") {
              return {
                success: true,
                fileName: file.name,
                status: "ready" as const,
              };
            } else {
              return {
                success: false,
                fileName: file.name,
                status: "pending" as const,
              };
            }
          }
        } catch (error) {
          return {
            success: false,
            fileName: file.name,
            status: "failed" as const,
          };
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      let successCount = uploadResults.filter((r) => r.success).length;
      const failedFiles = uploadResults
        .filter((r) => !r.success && r.status === "failed")
        .map((r) => r.fileName);
      const failedStatus = uploadResults.some((r) => r.status === "pending");

      if (failedStatus) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        const getReferralResult = await this.getReferralById(referralId);
        if (getReferralResult.isErr) {
          return Result.err(new Error("Failed to get referral details"));
        }
        const referral = getReferralResult.value;
        if (
          referral.attachments.every(
            (attachment) => attachment.status === "ready"
          )
        ) {
          successCount = referral.attachments.length;
        }
      }

      const isSuccess = successCount === files.length;

      const result: AttachmentUploadResult = {
        success: isSuccess,
        message:
          successCount > 0
            ? `Successfully uploaded ${successCount}/${files.length} attachments`
            : undefined,
        error: isSuccess
          ? undefined
          : `Failed to upload ${failedFiles.join(", ")}`,
      };

      return isSuccess
        ? Result.ok(result)
        : Result.err(new Error("Failed to upload attachments"));
    } catch (error) {
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to upload attachments")
      );
    }
  }

  async getReferralById(
    referralId: string
  ): Promise<Result<AccelerantReferral, Error>> {
    try {
      const apiUrl = `${config.accelerant.getReferralUrl}/${referralId}`;

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
      });

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          console.error("Could not parse error response");
        }
        return Result.err(
          new Error(
            `Failed to fetch referral: HTTP ${response.status}. ${errorDetails}`
          )
        );
      }

      const result = await response.json();
      return Result.ok(result);
    } catch (error) {
      return Result.err(
        error instanceof Error ? error : new Error("Failed to fetch referral")
      );
    }
  }

  async updateReferralStatus(
    referralId: string,
    status: string
  ): Promise<Result<ReferralStatusUpdateResult, Error>> {
    try {
      const apiUrl = `${config.accelerant.updateReferralUrl}/${referralId}`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify({
          state: status,
        }),
      });

      if (!response.ok) {
        let errorDetails = "";
        try {
          const errorBody = await response.text();
          errorDetails = errorBody;
        } catch (e) {
          console.error("Could not parse error response");
        }
        return Result.err(
          new Error(
            `Failed to update referral status: HTTP ${response.status}. ${errorDetails}`
          )
        );
      }

      return Result.ok({
        success: true,
        message: `Successfully updated referral status to ${status}`,
      });
    } catch (error) {
      console.error("Failed to update referral status", error);
      return Result.err(
        error instanceof Error
          ? error
          : new Error("Failed to update referral status")
      );
    }
  }
}

export const accelerantService = new AccelerantService();
